import { isAfter } from "date-fns";

import { config } from "config/config";
import { ApiManager } from "services/api";
import { IAgent } from "store/entities/agents";

import { IAnalyticsIdentity } from "./analytics";

declare global {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: (...args: any[]) => void;
  }
}

window.dataLayer = window.dataLayer || [];

// Cutoff for the new way of sending the signup event.
// Only licenses created after this date will have the new event sent.
const SIGNUP_EVENT_CUTOFF = "2024-11-14T11:00:00.000Z"; // 12AM GMT+1

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function installGTM(gtmId: string) {
  const gtmScriptId = "gtm-script";
  const gtmNoscriptId = "gtm-noscript";

  if (!document.getElementById(gtmScriptId)) {
    const script = document.createElement("script");
    script.id = gtmScriptId;
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.appendChild(script);
  }

  if (!document.getElementById(gtmNoscriptId)) {
    const noscript = document.createElement("noscript");
    noscript.id = gtmNoscriptId;
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.appendChild(noscript);
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    script.onload = () => resolve();
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    script.onerror = () => reject();
    document.head.appendChild(script);
  });
}

function sendEvent(event: object): void {
  if (["labs", "dev"].includes(config.env)) {
    // eslint-disable-next-line no-console
    console.log("[Google Analytics]", event);
  }
  window.dataLayer.push(event);
}

// FIXME: Fix this the next time the file is edited.
// @ts-expect-error TS(6133): 'gtag' is declared but its value is never read.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
function gtag(...args: any[]) {
  window.dataLayer.push(args);
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function fetchScript() {
  const scriptSrc = `https://www.googletagmanager.com/gtm.js?id=${config.googleTagManagerId}`;
  return loadScript(scriptSrc).catch(() => {});
}

function eventStart(): void {
  sendEvent({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });
}

// The signup event should be sent only:
// - If the license was created after the cutoff date
// - If the user is an admin (and thus can set the license flag to prevent multiple events from being sent)
// - If the signup event hasn't been sent yet (per license flag)
export const shouldSendSignupEvent = (
  licenseCreatedAt: Date,
  isAdmin: boolean,
  hasSentSignupGoogleAnalyticsEvent: boolean
): boolean => {
  return isAfter(licenseCreatedAt, new Date(SIGNUP_EVENT_CUTOFF)) && isAdmin && !hasSentSignupGoogleAnalyticsEvent;
};

export function eventSignup(identity: IAnalyticsIdentity, userType: string, licenseCreatedAt: Date): void {
  // From https://docs.google.com/spreadsheets/d/1kmYUc7mtXJAHXajwz-V6HKT6jkPl3L2xDDAAdd6FboQ/edit?gid=603526609#gid=603526609 (HD GA docs)
  // Signup should only fire ONCE per license, the first time an admin logs in. The license_created_at is the date of license creation (the actual signup date)
  // as the GA event will fire later than that.
  sendEvent({
    event: "Signup",
    user_id: identity.license_id.toString(),
    user_type: userType,
    license_created_at: licenseCreatedAt.getTime().toString(),
  });
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
async function loadGtagScript() {
  const gtagScriptSrc = `https://www.googletagmanager.com/gtag/js?id=${config.GA4PropertyId}`;
  await loadScript(gtagScriptSrc);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  window.gtag = function () {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag("js", new Date());
  window.gtag("config", config.GA4PropertyId);
}

async function updateAccountGA(loggedInAgent: IAgent): Promise<void> {
  try {
    const [clientId, sessionId] = await Promise.all([
      new Promise<string>(resolve => {
        window.gtag("get", config.GA4PropertyId, "client_id", (id: string) => {
          resolve(id);
        });
      }),
      new Promise<string>(resolve => {
        window.gtag("get", config.GA4PropertyId, "session_id", (id: string) => {
          resolve(id);
        });
      }),
    ]);

    if (clientId && sessionId) {
      const agentSettings = loggedInAgent.settings || {};
      const googleAnalytics = agentSettings?.googleAnalytics;
      const { clientID: prevClientID, sessionID: prevSessionID } = googleAnalytics || {};

      if (clientId === prevClientID && sessionId === prevSessionID?.toString()) {
        return;
      }

      await ApiManager.agentApi.updateAgent(loggedInAgent.id, {
        settings: {
          googleAnalytics: {
            clientID: clientId,
            sessionID: sessionId,
          },
        },
      });
    } else {
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.error("Failed to get GA4 clientID and sessionID");
    }
  } catch (error) {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line no-console
    console.error("Failed to fetch GA data", error);
  }
}
export async function initializeGTM(loggedInAgent: IAgent): Promise<void> {
  await fetchScript();
  await loadGtagScript();
  eventStart();
  updateAccountGA(loggedInAgent);
}
